import { selectPrimaryRecommendation } from '@/lib/app-categories/discontinued'
import { type ReactNode } from 'react'
import {
  Classification,
  Recommendation,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { PartnershipTier } from '@/collections/App/constants'
import { processAffiliateLink } from '@/lib/formatting'
import { isPopulatedObject } from '@/lib/utils'
import { createLogger, LogLevel } from '@/lib/logger'
import type { App, AppCategoryRecommendation, Category, Review } from '@/payload-types'

const logger = createLogger('App Helpers', {
  level: process.env.NODE_ENV === 'development' ? LogLevel.Warn : LogLevel.Silent,
})
const loggedPrimarySentinelIds = new Set<string>()

// Narrower than determinePremiumPartner (which also counts Silver/Sponsored):
// only the top two tiers. Used to hide the generic "Top deals right now" slot on
// an app's own page when that app is a Gold/Platinum partner — its own deal/CTA
// already leads, so the cross-sell list is redundant.
export function isGoldOrPlatinumPartner(partnershipTier: App['partnershipTier']): boolean {
  return partnershipTier === PartnershipTier.Gold || partnershipTier === PartnershipTier.Platinum
}

export function determinePremiumPartner(partnershipTier: App['partnershipTier']): boolean {
  const premiumPartnerTiers: PartnershipTier[] = [
    PartnershipTier.Platinum,
    PartnershipTier.Gold,
    PartnershipTier.Silver,
    PartnershipTier.Sponsored,
  ]
  return !!partnershipTier && premiumPartnerTiers.includes(partnershipTier as PartnershipTier)
}

// Pick one partner weighted by tier. Weights are RELATIVE (need not sum to 100), so there's
// nothing to validate or auto-fill. A tier with no candidates is skipped and its weight
// redistributes to the rest, so "no gold -> platinum 100%" falls out for free. Default is
// platinum 60 / gold 40. Client-only (uses Math.random), so the pick re-rolls per render.
export function pickWeightedPartner<T extends { partnershipTier?: App['partnershipTier'] }>(
  candidates: T[],
  weights: Partial<Record<PartnershipTier, number>> = {
    [PartnershipTier.Platinum]: 60,
    [PartnershipTier.Gold]: 40,
  },
): T | undefined {
  const byTier = new Map<PartnershipTier, T[]>()
  for (const candidate of candidates) {
    const tier = candidate.partnershipTier as PartnershipTier | null | undefined
    if (!tier) continue
    const weight = weights[tier]
    if (!weight || weight <= 0) continue
    const existing = byTier.get(tier)
    if (existing) existing.push(candidate)
    else byTier.set(tier, [candidate])
  }

  const buckets = [...byTier.entries()].map(([tier, items]) => ({
    items,
    weight: weights[tier] ?? 0,
  }))
  if (buckets.length === 0) return undefined

  const totalWeight = buckets.reduce((sum, bucket) => sum + bucket.weight, 0)
  let roll = Math.random() * totalWeight
  for (const bucket of buckets) {
    if (roll < bucket.weight) {
      return bucket.items[Math.floor(Math.random() * bucket.items.length)]
    }
    roll -= bucket.weight
  }

  // Floating-point fallback: pick from the last bucket.
  const last = buckets[buckets.length - 1]
  return last.items[Math.floor(Math.random() * last.items.length)]
}

type PrimaryButtonParams = {
  app: Pick<App, 'slug' | 'affiliateLink' | 'name' | 'partnershipTier'>
  deal: {
    count: number
    label?: ReactNode
    labelShort?: ReactNode
  }
  compareWithApp?: { slug?: string | null }
  // Canonical stored comparison slug for (app, compareWithApp). When set, the compare
  // CTA uses it verbatim to avoid the non-canonical-order 307 redirect.
  canonicalCompareSlug?: string
  categoryApps?: string[]
  isSummary?: boolean
  isBest?: boolean
  isRecommended?: boolean
  isBroadCategory?: boolean
  isAlternative?: boolean
  desktopIsRequired?: boolean
  isDesktop?: boolean
}

// Renders the desktop label and the < md ("mobile") label as siblings, toggled by pure
// CSS — replaces a per-card useMediaQuery (the original `(min-width: 768px)` query) that
// caused a hydration flip on every app card. Both are in the DOM; `hidden`/`md:hidden`
// (display:none) drops the inactive one, so server and client render identically.
function responsiveLabel(full: ReactNode, short: ReactNode): ReactNode {
  if (full === short) return full
  return (
    <>
      <span className="hidden md:inline">{full}</span>
      <span className="md:hidden">{short}</span>
    </>
  )
}

export function getPrimaryButton({
  app,
  deal,
  compareWithApp,
  canonicalCompareSlug,
  categoryApps,
  isSummary = false,
  isBest = false,
  isRecommended = false,
  isBroadCategory = false,
  isAlternative = false,
  desktopIsRequired = false,
  isDesktop = true,
}: PrimaryButtonParams): {
  destination: string
  label: ReactNode
  isReadReview: boolean
  icon: string
  opensInNewTab: boolean
  passReferrerUrl?: boolean
  isDeal?: boolean
  sponsored?: boolean
  desktopIsRequired?: boolean
} {
  const affiliate = processAffiliateLink(app.affiliateLink, app.slug, app.name, app.partnershipTier)

  const isPremiumPartner = determinePremiumPartner(app.partnershipTier)
  const isBronzeAffiliate = app.partnershipTier === PartnershipTier.Bronze

  const categoryHasPremiumPartner =
    categoryApps &&
    categoryApps.length > 0 &&
    categoryApps.some((tier) => determinePremiumPartner(tier as App['partnershipTier']))

  const canPromoteOutbound =
    isPremiumPartner ||
    (isBronzeAffiliate && isBest) ||
    (isBronzeAffiliate && isRecommended && !categoryHasPremiumPartner)

  // One deal -> the deal's own label; multiple -> "Claim deals"; none -> a generic prompt.
  // labelShort (no app-name suffix) is the < md variant, toggled by CSS in responsiveLabel.
  const dealFull = deal.label
  const dealShort = deal.labelShort ?? deal.label
  const outboundFull =
    deal.count === 1 ? dealFull : deal.count > 1 ? `${dealFull} + deal` : `Try ${app.name}`
  const outboundShort = deal.count === 1 ? dealShort : outboundFull

  // Non-partner summary CTA target, in priority order: resolved canonical comparison slug,
  // then an ad-hoc <topPick>-vs-<app> pair, then the single-app picker.
  function buildCompareDestination(): string {
    if (isBroadCategory) return `/compare/${app.slug}`
    if (canonicalCompareSlug) return `/compare/${canonicalCompareSlug}`
    if (compareWithApp?.slug) return `/compare/${compareWithApp.slug}-vs-${app.slug}`
    return `/compare/${app.slug}`
  }

  if (canPromoteOutbound) {
    // Desktop-only deals fall back to the internal deals page on tablet/mobile instead of
    // sending the click straight to the (desktop-only) affiliate destination.
    const useDealFallback = desktopIsRequired && !isDesktop
    return {
      destination: useDealFallback ? `/deals/${app.slug}` : affiliate.url,
      label: responsiveLabel(outboundFull, outboundShort),
      isReadReview: false,
      icon: 'site-large',
      opensInNewTab: !useDealFallback,
      passReferrerUrl: useDealFallback ? undefined : affiliate.passReferrerUrl,
      isDeal: useDealFallback ? false : deal.count > 0,
      sponsored: useDealFallback ? undefined : affiliate.isSponsored,
      desktopIsRequired,
    }
  }

  if (isBest) {
    const full = isSummary
      ? isAlternative
        ? `Compare ${app.name}`
        : 'Compare alternatives'
      : 'Read review'
    return {
      destination: isSummary
        ? isAlternative
          ? buildCompareDestination()
          : `/alternatives/${app.slug}`
        : `/apps/${app.slug}`,
      label: responsiveLabel(full, isSummary ? 'Compare' : 'Read review'),
      isReadReview: !isSummary,
      icon: 'read-review',
      opensInNewTab: false,
    }
  }

  const defaultFull = isSummary
    ? isAlternative
      ? `Compare ${app.name}`
      : 'Compare'
    : 'Read review'
  return {
    destination: isSummary ? buildCompareDestination() : `/apps/${app.slug}`,
    label: responsiveLabel(defaultFull, isSummary ? 'Compare' : 'Read review'),
    isReadReview: !isSummary,
    icon: 'read-review',
    opensInNewTab: false,
  }
}

export function getAppRecommendation({
  categoryId,
  app,
  appCategoryRecommendation,
}: {
  categoryId: Category['id']
  app: Pick<App, 'categoryRecommendations'> & {
    review?: App['review']
    description?: App['description']
    id?: App['id']
    name?: App['name']
    slug?: App['slug']
  }
  appCategoryRecommendation?: Pick<
    AppCategoryRecommendation,
    'recommendation' | 'classification' | 'bestFor'
  > | null
}): {
  recommendation: Recommendation
  bestFor: AppCategoryRecommendation['bestFor']
} {
  const docs = app.categoryRecommendations?.docs ?? []
  const primary = selectPrimaryRecommendation(
    docs.filter(isPopulatedObject<AppCategoryRecommendation>),
  )

  // categoryId 0 is a sentinel for "match the app's primary category".
  const acr =
    appCategoryRecommendation ??
    docs.find((rec): rec is AppCategoryRecommendation => {
      if (!isPopulatedObject<AppCategoryRecommendation>(rec)) return false

      if (categoryId === 0) {
        if (
          !isPopulatedObject<Category>(rec.category) &&
          rec.classification === Classification.Primary
        ) {
          const logKey = `${app.id ?? 'unknown-app'}:${rec.id}`
          if (!loggedPrimarySentinelIds.has(logKey)) {
            loggedPrimarySentinelIds.add(logKey)
            logger.warn('Resolved primary recommendation from ID-only category relation', {
              appId: app.id,
              appName: app.name,
              appSlug: app.slug,
              appCategoryRecommendationId: rec.id,
              category: rec.category,
            })
          }
        }
        return rec.id === primary?.id
      }

      const cat = rec.category
      const catId = isPopulatedObject<Category>(cat) ? cat.id : cat
      return catId === categoryId
    })

  const recommendation = acr?.recommendation
  const review = (app.review?.docs?.[0] ?? null) as Review | null

  const acrBest = (acr?.bestFor ?? '').trim()
  const reviewBest = (review?.bestFor ?? '').trim()
  const bestFor =
    acrBest !== '' ? acrBest : reviewBest !== '' ? reviewBest : app.description ?? null

  // Silently return NotRecommended when no recommendation found
  // This is expected in comparison pages where apps may not share all categories
  return {
    recommendation: (recommendation as Recommendation) ?? Recommendation.NotRecommended,
    bestFor,
  }
}
